import { useEffect } from 'react';
import { SITE_ORIGIN, LOCAL_BUSINESS_LD } from '../data/business';

/* ============================================================
   Seo — gestion du <head> par page, sans dépendance.
   Même technique impérative que LegalLayout (mutation + restauration
   du DOM), pour rester homogène avec le reste du site et être capturée
   telle quelle par le prérendu (react-snap).

   Injecte le JSON-LD propre à la page : Service (provider = LocalBusiness),
   FAQPage et BreadcrumbList. Retire au passage le FAQPage et le
   BreadcrumbList "home" hérités de index.html pour éviter les conflits.
   ============================================================ */

const setMetaContent = (selector, content) => {
  const el = document.querySelector(selector);
  if (!el || content == null) return null;
  const previous = el.getAttribute('content');
  el.setAttribute('content', content);
  return () => el.setAttribute('content', previous);
};

const setLinkHref = (selector, href) => {
  const el = document.querySelector(selector);
  if (!el) return null;
  const previous = el.getAttribute('href');
  el.setAttribute('href', href);
  return () => el.setAttribute('href', previous);
};

const Seo = ({ title, description, canonicalPath, service, faq = [], breadcrumb = [] }) => {
  const depsKey = JSON.stringify({ title, description, canonicalPath, service, faq, breadcrumb });
  useEffect(() => {
    const url = `${SITE_ORIGIN}${canonicalPath}`;
    const previousTitle = document.title;
    document.title = title;
    window.scrollTo(0, 0);

    // 1. Balises meta / canonical / OG / Twitter (avec restauration)
    const restorers = [
      setMetaContent('meta[name="description"]', description),
      setLinkHref('link[rel="canonical"]', url),
      setMetaContent('meta[property="og:url"]', url),
      setMetaContent('meta[property="og:title"]', title),
      setMetaContent('meta[property="og:description"]', description),
      setMetaContent('meta[name="twitter:title"]', title),
      setMetaContent('meta[name="twitter:description"]', description),
    ].filter(Boolean);

    // 2. Retirer les JSON-LD "home" (FAQPage + BreadcrumbList) le temps de la page
    const homeLd = Array.from(
      document.querySelectorAll('script[type="application/ld+json"]')
    ).filter((s) => {
      const t = s.textContent || '';
      return t.includes('"FAQPage"') || t.includes('"BreadcrumbList"');
    });
    const detached = homeLd.map((s) => ({ node: s, parent: s.parentNode, next: s.nextSibling }));
    detached.forEach(({ node }) => node.remove());

    // 3. Construire le JSON-LD de la page
    const graph = [];

    if (service) {
      graph.push({
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: service.name,
        serviceType: service.serviceType || service.name,
        description: service.description || description,
        url,
        areaServed: { '@type': 'City', name: 'Montpellier' },
        provider: { '@context': 'https://schema.org', ...LOCAL_BUSINESS_LD },
        ...(service.offers ? { offers: service.offers } : {}),
      });
    }

    if (faq.length) {
      graph.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      });
    }

    if (breadcrumb.length) {
      graph.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumb.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: `${SITE_ORIGIN}${b.path || canonicalPath}`,
        })),
      });
    }

    const injected = graph.map((obj) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-page', '1');
      script.textContent = JSON.stringify(obj);
      document.head.appendChild(script);
      return script;
    });

    // 4. Nettoyage au démontage : restaurer le head d'origine
    return () => {
      document.title = previousTitle;
      restorers.forEach((restore) => restore && restore());
      injected.forEach((s) => s.remove());
      detached.forEach(({ node, parent }) => {
        if (parent) parent.appendChild(node);
      });
    };
  }, [depsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default Seo;
